import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { allTrackers } from "app/services/track/track";
import styled from "styled-components";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { KeywordCompetitorsPageScatterChart } from "app/pages/keyword-research/website-analysis/pages/search-competitors/KeywordCompetitorsPageScatterChart";
import type { IKeywordCompetitorsPageContext } from "./KeywordCompetitorsPageContext";
import { KeywordCompetitorsPageContextProvider } from "./KeywordCompetitorsPageContext";
import { WebsiteTypeFilter } from "./filters/WebsiteTypeFilter";
import {
    NewCompetitorsFilter,
    RisingCompetitorsFilter,
} from "app/pages/keyword-research/website-analysis/pages/search-competitors/filters/CompetitorsFilters";
import { CategoryFilter } from "app/pages/keyword-research/website-analysis/pages/search-competitors/filters/CategoryFilter";
import { SearchTypeFilter } from "app/pages/keyword-research/website-analysis/pages/search-competitors/filters/SearchTypeFilter";
import { SearchFilter } from "app/pages/keyword-research/website-analysis/pages/search-competitors/filters/SearchFilter";
import { ChipItemWrapper } from "@similarweb/ui-components";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { useCurrentStateAndParams } from "@uirouter/react";
import DurationService from "app/services/DurationService";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { translate } from "app/filters/i18nFilter";
import { formatDate } from "@similarweb/pro-logic-components";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";

const TopStyled = styled(FlexRow)`
    justify-content: space-between;
    padding: 12px;
    padding-bottom: 2px;
`;
const TopStyledLeft = styled(FlexRow)`
    align-items: center;
    flex-wrap: wrap;
`;
const ChipWrap = styled.div`
    padding-left: 8px;
    margin-bottom: 10px;
    &:first-child {
        padding-left: 0;
    }
    ${ChipItemWrapper} {
        margin-right: 0;
    }
`;

const ChipWrapLastInRow = styled(ChipWrap)`
    margin-right: 8px;
`;

const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;

const TableHeader = styled.div`
    ${setFontVariant("h5_2", colorsPalettes.carbon[500])};
    padding: 20px 24px;
`;

export const KeywordCompetitorsTableTop: FunctionComponent<any> = (props) => {
    const {
        isLoadingData,
        isCompare,
        downloadExcelPermitted,
        excelLink,
        tableColumns,
        onClickToggleColumns,
        filtersStateObject,
        onFilterChange,
        mainSiteData,
        totalCount,
        domainsCountApi,
    } = props;
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const selectedSite = filtersStateObject.selectedSite || filtersStateObject.keys;
    const trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
    const { params } = useCurrentStateAndParams();
    const { webSource, isWWW, country, key, duration } = params;
    const durationObject = DurationService.getDurationData(duration);
    const { from, to } = durationObject.forAPI;

    const queryParamsForCompareTabs = {
        from,
        to,
        webSource,
        isWWW: isWWW !== "*",
        keys: key,
        country,
        includeSubdomains: true,
    };
    const dateFilter = useSubtitleFilters("date");
    const FormattedDate = formatDate(dateFilter[0]?.value?.from, dateFilter[0]?.value?.to);

    const onTabSelectSite = (selectedSite, numberOfResults) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite || ""}/${numberOfResults || ""}`);
        proNavigator.applyUpdateParams({ selectedSite: selectedSite });
        onFilterChange({ selectedSite: selectedSite });
    };
    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const onColumnToggle = (key) => {
        onClickToggleColumns(parseInt(key));
    };

    const scatterChartData = Array.isArray(props.tableData)
        ? props.tableData.slice(0, 10).filter((item) => item.Domain !== "grid.upgrade")
        : null;

    const context = useMemo<IKeywordCompetitorsPageContext>(() => {
        const context: IKeywordCompetitorsPageContext = {
            allSites: chosenSites.map((item, infoItem) => {
                return { name: item, displayName: infoItem.displayName, icon: infoItem.icon };
            }),
            selectedSite: filtersStateObject.selectedSite,
            onSelectSite: (selectedSite) => {
                const value = { selectedSite: selectedSite.text };
                allTrackers.dispatchTrackEvent("Drop Down", "click", `select domain/${selectedSite.text || ""}`);
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
            onSelectRisingCompetitors: () => {
                const value = { risingCompetitors: !filtersStateObject.risingCompetitors };
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "check",
                    `${filtersStateObject.risingCompetitors ? "remove" : "add"} rising competitors`,
                );
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
            risingCompetitors: filtersStateObject.risingCompetitors,
            onSelectNewCompetitors: () => {
                const value = { newCompetitors: !filtersStateObject.newCompetitors };
                allTrackers.dispatchTrackEvent(
                    "Filter Table",
                    "check",
                    `${filtersStateObject.newCompetitors ? "remove" : "add"} "new" competitors`,
                );
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
            newCompetitors: filtersStateObject.newCompetitors,
            onSelectWebsiteType: (type) => {
                const value = { websiteType: type?.id };
                allTrackers.dispatchTrackEvent(
                    "Drop Down",
                    "click",
                    `select website type/${(type?.id ?? "clear") || ""}`,
                );
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
            selectedWebsiteType: filtersStateObject.websiteType,
            allCategories: props.allCategories,
            selectedCategory: filtersStateObject.category,
            onSelectCategory: (category) => {
                const value = { category: category?.forApi };
                allTrackers.dispatchTrackEvent(
                    "Drop Down",
                    "click",
                    `select category/${(category?.id ?? "clear") || ""}`,
                );
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
            // search types
            searchTypes: props.searchTypes,
            selectedSearchType: filtersStateObject.searchType,
            onSelectSearchType: (type) => {
                const value = { [props.searchTypeParam]: type?.id };
                allTrackers.dispatchTrackEvent(
                    "Drop Down",
                    "click",
                    `select search type/${(type?.id ?? "clear") || ""}`,
                );
                proNavigator.applyUpdateParams(value);
                onFilterChange({ searchType: type?.id });
            },
            searchTypeFilterPlaceholder: props.searchTypeFilterPlaceholder,
            search: filtersStateObject.search,
            onSearch: (term) => {
                const value = { search: term || null };
                proNavigator.applyUpdateParams(value);
                onFilterChange(value);
            },
        };
        return context;
    }, [filtersStateObject, onFilterChange, props.allCategories, props.searchTypes, props.searchTypeParam]);
    return isLoadingData ? null : (
        <KeywordCompetitorsPageContextProvider value={context}>
            <TableHeader>
                {translate("website.competitors.table.header.title")} {FormattedDate}
            </TableHeader>
            {isCompare && (
                <CompareTabsContainer
                    onSelectSite={onTabSelectSite}
                    keyName="PaidCompetitorsFirstLoad"
                    api={domainsCountApi}
                    domainQueryParamName={"selectedSite"}
                    queryParamsForCompareTabs={queryParamsForCompareTabs}
                />
            )}
            <TopStyled>
                <TopStyledLeft>
                    <ChipWrap>
                        <CategoryFilter />
                    </ChipWrap>
                    <ChipWrap>
                        <WebsiteTypeFilter />
                    </ChipWrap>
                    <ChipWrapLastInRow>
                        <SearchTypeFilter />
                    </ChipWrapLastInRow>
                    <FlexRow
                        style={{ height: 32 }}
                        alignItems="center"
                    >
                        <ChipWrap>
                            <RisingCompetitorsFilter />
                        </ChipWrap>
                        <ChipWrap>
                            <NewCompetitorsFilter />
                        </ChipWrap>
                    </FlexRow>
                </TopStyledLeft>
            </TopStyled>
            {mainSiteData && scatterChartData?.length > 0 && (
                <KeywordCompetitorsPageScatterChart
                    columns={props.tableColumns
                        .filter((column) => column.field && column.scatterChart)
                        .map((column) => {
                            return {
                                id: column.field,
                                name: column.displayName,
                                formatter: column.scatterChartFormatter,
                                disableBenchMark: !column.scatterChartBenchmark,
                                hidePill: column.hideScatterTooltipPill,
                            };
                        })}
                    tableData={scatterChartData}
                    domain={selectedSite}
                    mainSiteData={mainSiteData}
                />
            )}
            <SearchContainer>
                <SearchFilter />
                <Right>
                    <FlexRow>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                totalCount={totalCount}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                            />
                        </DownloadExcelContainer>
                        <div>
                            <ColumnsPickerLite
                                {...getColumnsPickerLiteProps()}
                                translations={{
                                    tooltip: translate("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        translate("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: translate("common.columnsPicker.mandatoryField"),
                                    apply: translate("common.apply"),
                                }}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </KeywordCompetitorsPageContextProvider>
    );
};
